import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar, ChevronRight } from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

const formatDateRange = (start, end) => {
  const s = new Date(start).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const e = new Date(end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return start === end ? e : `${s} – ${e}`;
};

const EventCard = ({ event, onClick }) => (
  <div
    onClick={onClick}
    style={{
      minWidth: 260,
      maxWidth: 280,
      flexShrink: 0,
      borderRadius: 16,
      overflow: "hidden",
      background: "#fff",
      border: "1px solid #fed7aa",
      boxShadow: "0 4px 16px rgba(249,115,22,0.10)",
      cursor: "pointer",
      transition: "transform 0.2s, box-shadow 0.2s",
      display: "flex",
      flexDirection: "column",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "0 12px 28px rgba(249,115,22,0.18)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 16px rgba(249,115,22,0.10)";
    }}
  >
    {/* Image */}
    <div style={{ position: "relative", height: 160, background: "#fff3e0", flexShrink: 0 }}>
      {event.cover_image ? (
        <img
          src={event.cover_image}
          alt={event.heading}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, #f97316, #ef4444)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 40,
          }}
        >
          🙏
        </div>
      )}
      {/* Date pill */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          fontSize: 11,
          fontWeight: 600,
          padding: "3px 10px",
          borderRadius: 20,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Calendar size={11} />
        {formatDateRange(event.start_date, event.end_date)}
      </div>
    </div>

    {/* Content */}
    <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", flex: 1 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: 14,
          color: "#1f2937",
          lineHeight: 1.4,
          marginBottom: 4,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {event.heading}
      </div>
      {event.sub_heading && (
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginBottom: 10,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {event.sub_heading}
        </div>
      )}

      <div style={{ marginTop: "auto" }}>
        {event.starting_price && (
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
            from{" "}
            <span style={{ fontWeight: 700, fontSize: 15, color: "#f97316" }}>
              ₹{event.starting_price}
            </span>
          </div>
        )}
        <button
          style={{
            width: "100%",
            background: "linear-gradient(to right, #f97316, #ef4444)",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "9px 0",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Book Now
        </button>
      </div>
    </div>
  </div>
);

const ViewAllCard = ({ onClick }) => (
  <div
    onClick={onClick}
    style={{
      minWidth: 200,
      maxWidth: 220,
      flexShrink: 0,
      borderRadius: 16,
      overflow: "hidden",
      background: "linear-gradient(135deg, #f97316, #ef4444)",
      boxShadow: "0 4px 16px rgba(249,115,22,0.25)",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px 20px",
      gap: 12,
      transition: "transform 0.2s, box-shadow 0.2s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "0 12px 28px rgba(249,115,22,0.35)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 16px rgba(249,115,22,0.25)";
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ArrowRight size={24} color="#fff" />
    </div>
    <div style={{ textAlign: "center" }}>
      <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>View All</div>
      <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 4 }}>
        Explore more pujas
      </div>
    </div>
  </div>
);

const EventsSection = () => {
  const navigate = useNavigate();
  const [eventsResponse, fetchEvents] = useApi("/user/events", null, API_METHODS.GET);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = eventsResponse?.data || [];

  // Don't render section if no active events
  if (!eventsResponse || eventsResponse.isLoading === null) return null;
  if (!events.length) return null;

  return (
    <section style={{ padding: "60px 0 52px", background: "#fff" }}>
      <div className="container mx-auto" style={{ paddingLeft: 24, paddingRight: 24 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div
            style={{
              display: "inline-block",
              background: "linear-gradient(to right, #f97316, #ef4444)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Special Events
          </div>
          <h2
            style={{
              fontSize: 32,
              fontWeight: 800,
              color: "#1f2937",
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            Upcoming Puja
          </h2>
          <p style={{ color: "#6b7280", marginTop: 8, fontSize: 15 }}>
            A spiritual ritual to seek blessings, and positive energy
          </p>
        </div>

        {/* Cards row — horizontal scroll on mobile */}
        <div
          style={{
            display: "flex",
            gap: 20,
            overflowX: "auto",
            paddingBottom: 12,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            justifyContent: events.length <= 3 ? "center" : "flex-start",
          }}
        >
          {events.slice(0, 6).map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onClick={() => navigate(`/event/${event.slug}`)}
            />
          ))}
        </div>

        {/* View all text link (below, for easy tap on mobile) */}
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <button
            onClick={() => navigate("/events")}
            style={{
              background: "none",
              border: "none",
              color: "#f97316",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            View all upcoming events <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <style>{`
        section div::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

export default EventsSection;
